import { mapActions, mapGetters } from 'vuex'

export default {
	mounted() {
		this.$el.addEventListener('click', this.handleInternalLinkClick)
	},
	beforeDestroy() {
		this.$el.removeEventListener('click', this.handleInternalLinkClick)
	},
	computed: {
		...mapGetters('settings', ['appCore']),
		...mapGetters('auth', ['mapRegisterToken']),
	},
	methods: {
		...mapActions('auth', ['getMapRegisterToken']),
		isLikelyHtml(input) {
			if (!input || typeof input !== 'string') return false
			return /<\/?[a-z][\s\S]*>/i.test(input) || /&[a-z]+;/.test(input)
		},
        stripBrAndEmptyParagraphs(html) {
            if (!html) return ''
            const parser = new DOMParser()
            const doc = parser.parseFromString(html, 'text/html')
            doc.body.querySelectorAll('p').forEach((p) => {
                const text = p.textContent.replace(/\u00a0/g, ' ').trim()
                if (!text.length && !p.querySelector('br')) p.remove()
            })
            doc.body.innerHTML = doc.body.innerHTML
                .replace(/(&nbsp;|\u00a0)+/g, ' ')
                .replace(/[ \t]{2,}/g, ' ')
                .replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br><br>')

            return doc.body.innerHTML
        },
		formatForumText(text) {
			if (!text) return ''
			let t = text
			if (this.isLikelyHtml(t)) {
				return t
			}
			t = t.replace(/^⸻$/gm, '<hr>')
			t = t.replace(/^[ \t]*•[ \t]+(.+)$/gm, '<li>$1</li>')
			t = t.replace(/((?:<li>.*?<\/li>\s*){1,})/g, '<ul>$1</ul>')
			t = t.replace(/^[ \t]*\d+\.[ \t]+(.+)$/gm, '<li>$1</li>')
			t = t.replace(/((?:<li>.*?<\/li>\s*){1,})/g, (match) => {
				return /\d+\./.test(match) ? `<ol>${match}</ol>` : match
			})
			t = t.replace(/\n/g, '<br>')
			t = t.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
			t = t.replace(/  /g, '&nbsp;&nbsp;')

			return t
		},
		postDescription(text) {
			if (!text) return ''
			const maybeFormatted = this.isLikelyHtml(text) ? text : this.formatForumText(text)

			const base = `${window.location.origin}/webapp`
			const deepTest = /^com\.pinxterapp\.dev\.[\w-]+:\/\//i
			const mapsTest = /^https:\/\/maps\.dev\.clowder\.com/i
			const urlTest = /^https?:\/\//

			const parser = new DOMParser()
			const doc = parser.parseFromString(maybeFormatted, 'text/html')
			doc.querySelectorAll('a').forEach((a) => {
				const raw = a.getAttribute('href') || a.textContent.trim()
				if (!raw) return

				if (deepTest.test(raw) || mapsTest.test(raw)) {
					const clean = deepTest.test(raw) ? raw.replace(deepTest, '') : raw
					a.href = '#'
					a.dataset.url = deepTest.test(raw) ? `${base}/${clean}` : clean
					a.classList.add('internal-link')
				} else if (urlTest.test(raw)) {
					a.href = raw
					a.target = '_self'
					a.rel = 'noopener noreferrer'
				}
				a.style.cssText = 'text-decoration: none; color: var(--link); cursor:pointer;'
			})
			const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false)
			const re =
				/(com\.pinxterapp\.dev\.[\w-]+:\/\/[^\s<>"']+|https:\/\/maps\.dev\.clowder\.com[^\s<>"']*|https?:\/\/[^\s<>"']+)/gi

			while (walker.nextNode()) {
				const node = walker.currentNode
				const val = node.nodeValue
				if (!re.test(val)) continue

				const frag = doc.createDocumentFragment()
				let last = 0
				val.replace(re, (match, _p1, offset) => {
					if (offset > last) {
						frag.appendChild(doc.createTextNode(val.slice(last, offset)))
					}
					const a = doc.createElement('a')
					if (/^com\.pinxterapp\.dev\./i.test(match) || mapsTest.test(match)) {
						const clean = /^com\.pinxterapp\.dev\./i.test(match)
							? match.replace(deepTest, '')
							: match
						a.href = '#'
						a.dataset.url = /^com\.pinxterapp\.dev\./i.test(match)
							? `${base}/${clean}`
							: clean
						a.className = 'internal-link'
					} else {
						a.href = match
						a.target = '_self'
						a.rel = 'noopener noreferrer'
					}
					a.textContent = match
					a.style.cssText = 'text-decoration: none; color: var(--link); cursor:pointer;'
					frag.appendChild(a)
					last = offset + match.length
				})
				if (last < val.length) frag.appendChild(doc.createTextNode(val.slice(last)))
				node.parentNode.replaceChild(frag, node)
			}
			const html = this.stripBrAndEmptyParagraphs(doc.body.innerHTML)
			return html
		},
		isUnsupportedDeepLink(link) {
			if (!link) return false
			let cleaned = link.replace(/^https?:\/\/[^/]+/i, '')
			if (cleaned.includes('://')) cleaned = cleaned.split('://')[1]
			cleaned = cleaned.replace(/^\/?webapp\/?/i, '')
			const firstSegment = cleaned.split(/[/?]/)[0]
			const unsupported = ['live-events', 'event-manager', 'higher-logic', 'gamification']
			return unsupported.includes(firstSegment)
		},

		isMapUrlValid(url) {
			try {
				const parsed = Array.isArray(this.appCore?.parsing_url)
					? this.appCore.parsing_url
					: JSON.parse(this.appCore?.parsing_url || '[]')
				const link = new URL(url)
				const origin = link.origin.replace(/\/+$/, '')
				return parsed.some((allowed) => (allowed || '').replace(/\/+$/, '') === origin)
			} catch (e) {
				console.error(e)
				return false
			}
		},

		async handleInternalLinkClick(event) {
			const a = event.target.closest('a.internal-link')
			if (!a) return

			event.preventDefault()
			event.stopPropagation()

			const url = a.dataset.url
			if (!url) return

			const unsupported = ['live-events', 'event-manager', 'higher-logic', 'gamification']
			const path = url.replace(`${window.location.origin}/webapp/`, '')
			const firstSeg = path.split(/[/?]/)[0]

			if (unsupported.includes(firstSeg)) {
				this.mobileLink = true
				return
			}

			if (this.isMapUrlValid(url)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) return
					const withToken = `${url}${url.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(withToken, '_self')
				} catch (e) {
					console.error(e)
				}
			} else {
				window.open(url, '_self')
			}
		},
	},
}
